import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import SchoolDetailsModal from '../../components/SchoolDetailsModal';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  Building2,
  BookOpen,
  FlaskConical,
  Home,
  Loader2,
  AlertCircle,
  Briefcase,
  Wrench,
  AlertTriangle,
  Building,
  Bed,
  Info,
  Sparkles,
  Clock,
  Search,
} from 'lucide-react';
import {
  getTotalClassrooms,
  getTotalLaboratories,
  getTotalLibraries,
  getTotalMultipurposeHalls,
  getClassroomsPmShriComparison,
  getTopSchoolsByInfra,
  getSchoolsNeedingAttention,
  getTotalAdministrativeRooms,
  getTotalSupportFacilities,
  getTotalStaffQuarters,
} from '../../services/api';
import { selectTotalSchools } from '../../../../../store/slices/pmShriConstantsSlice';

// ─── Chart colour palette ─────────────────────────────────────────────────────
const CHART_COLORS = [
  '#6A1786',
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#F43F5E',
  '#14B8A6',
  '#6366F1',
];

/** Format a number with locale commas, or '—' when unavailable */
const fmt = (n: any): string =>
  n != null && n !== '' ? Number(n).toLocaleString() : '—';

// ─────────────────────────────────────────────────────────────────────────────
export default function InfrastructureAnalysis() {
  const navigate = useNavigate();
  const totalSchoolsFromRedux = useSelector(selectTotalSchools);

  const [activeTab, setActiveTab] = useState<
    'overview' | 'academic' | 'labs' | 'admin_support' | 'residential_assembly' | 'gap_analysis'
  >('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Real API data states ───────────────────────────────────────────────────
  // GET /kvis/totalclassroomskvs
  const [classroomsApiData, setClassroomsApiData] = useState<any>(null);
  // GET /kvis/totallaboratoriessciencecomputerlabs
  const [labsApiData, setLabsApiData] = useState<any>(null);
  // GET /kvis/totallibraries
  const [librariesApiData, setLibrariesApiData] = useState<any>(null);
  // GET /kvis/totalmultipurposehallsassemblyevents
  const [hallsApiData, setHallsApiData] = useState<any>(null);
  // GET /kvis/classrooms-pmshri-nonpmshri
  const [comparisonApiData, setComparisonApiData] = useState<any>(null);
  // GET /kvis/topschools-infra?wise=laboratories&order=desc
  const [topLabSchools, setTopLabSchools] = useState<any[]>([]);
  // GET /kvis/topschools-infra?wise=laboratories&order=asc
  const [lowLabSchools, setLowLabSchools] = useState<any[]>([]);
  // GET /kvis/topschools-infra?wise=classroomsinfra&order=asc
  const [lowClassroomSchools, setLowClassroomSchools] = useState<any[]>([]);

  // New API data states
  const [adminRoomsApiData, setAdminRoomsApiData] = useState<any>(null);
  const [supportFacilitiesApiData, setSupportFacilitiesApiData] = useState<any>(null);
  const [staffQuartersApiData, setStaffQuartersApiData] = useState<any>(null);

  // New gap analysis states
  const [topAdminSchools, setTopAdminSchools] = useState<any[]>([]);
  const [lowAdminSchools, setLowAdminSchools] = useState<any[]>([]);
  const [topSupportSchools, setTopSupportSchools] = useState<any[]>([]);
  const [lowSupportSchools, setLowSupportSchools] = useState<any[]>([]);
  const [topQuartersSchools, setTopQuartersSchools] = useState<any[]>([]);
  const [lowQuartersSchools, setLowQuartersSchools] = useState<any[]>([]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [gapWise, setGapWise] = useState<
    'classrooms' | 'labs' | 'adminrooms' | 'supportfacilities' | 'quarters'
  >('classrooms');
  const [gapSearch, setGapSearch] = useState('');
  const [selectedSchool, setSelectedSchool] = useState<{
    schoolID: number;
    schoolName: string;
  } | null>(null);

  // ── Fetch all real API data ───────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const wrapSafe = async (promise: Promise<any>, fallback: any = null) => {
          try {
            return await promise;
          } catch (err) {
            console.error('API call failed:', err);
            return fallback;
          }
        };

        const [
          cls,
          labs,
          libs,
          halls,
          comparison,
          topLabs,
          lowLabs,
          lowClass,
          adminRooms,
          supportFacilities,
          staffQuarters,
          topAdmin,
          lowAdmin,
          topSupport,
          lowSupport,
          topQuarters,
          lowQuarters,
        ] = await Promise.all([
          wrapSafe(getTotalClassrooms()),
          wrapSafe(getTotalLaboratories()),
          wrapSafe(getTotalLibraries()),
          wrapSafe(getTotalMultipurposeHalls()),
          wrapSafe(getClassroomsPmShriComparison()),
          wrapSafe(getTopSchoolsByInfra('laboratories', 1)),
          wrapSafe(getSchoolsNeedingAttention('laboratories', 1)),
          wrapSafe(getSchoolsNeedingAttention('classroomsinfra', 1)),
          wrapSafe(getTotalAdministrativeRooms()),
          wrapSafe(getTotalSupportFacilities()),
          wrapSafe(getTotalStaffQuarters()),
          wrapSafe(getTopSchoolsByInfra('adminrooms', 1)),
          wrapSafe(getSchoolsNeedingAttention('adminrooms', 1)),
          wrapSafe(getTopSchoolsByInfra('supportfacilities', 1)),
          wrapSafe(getSchoolsNeedingAttention('supportfacilities', 1)),
          wrapSafe(getTopSchoolsByInfra('quarters', 1)),
          wrapSafe(getSchoolsNeedingAttention('quarters', 1)),
        ]);

        setClassroomsApiData(cls);
        setLabsApiData(labs);
        setLibrariesApiData(libs);
        setHallsApiData(halls);
        setComparisonApiData(comparison);
        setTopLabSchools(Array.isArray(topLabs) ? topLabs : []);
        setLowLabSchools(Array.isArray(lowLabs) ? lowLabs : []);
        setLowClassroomSchools(Array.isArray(lowClass) ? lowClass : []);

        setAdminRoomsApiData(adminRooms);
        setSupportFacilitiesApiData(supportFacilities);
        setStaffQuartersApiData(staffQuarters);
        setTopAdminSchools(Array.isArray(topAdmin) ? topAdmin : []);
        setLowAdminSchools(Array.isArray(lowAdmin) ? lowAdmin : []);
        setTopSupportSchools(Array.isArray(topSupport) ? topSupport : []);
        setLowSupportSchools(Array.isArray(lowSupport) ? lowSupport : []);
        setTopQuartersSchools(Array.isArray(topQuarters) ? topQuarters : []);
        setLowQuartersSchools(Array.isArray(lowQuarters) ? lowQuarters : []);
      } catch (err: any) {
        console.error('Infrastructure fetch error:', err);
        setError(err.message || 'Failed to load infrastructure data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ── Derived: lab-type chart data ──────────────────────────────────────────
  // Source: /kvis/totallaboratoriessciencecomputerlabs → data.data[]
  const labTypeChartData = useMemo(() => {
    if (!labsApiData?.data) return [];
    return (labsApiData.data as { name: string; guide: number }[]).map(
      (item) => ({ name: item.name, Total: item.guide })
    );
  }, [labsApiData]);

  // ── Derived: classroom-type breakdown (A/B/C/D/Others) ───────────────────
  // Source: /kvis/classrooms-pmshri-nonpmshri → PMShree + Non-PMShree breakdown
  const classroomBreakdownData = useMemo(() => {
    if (!comparisonApiData) return [];
    const pm = comparisonApiData?.PMShree?.Classroom_Breakdown || {};
    const non = comparisonApiData?.['Non-PMShree']?.Classroom_Breakdown || {};
    return ['Type_A', 'Type_B', 'Type_C', 'Type_D', 'Others'].map((key) => ({
      name: key.replace('_', ' '),
      'PM-SHRI': pm[key] || 0,
      'Non PM-SHRI': non[key] || 0,
    }));
  }, [comparisonApiData]);

  // ── Derived: assembly/halls pie data ─────────────────────────────────────
  // Source: /kvis/totalmultipurposehallsassemblyevents → data.data[]
  const assemblyPieData = useMemo(() => {
    if (!hallsApiData?.data) return [];
    return (hallsApiData.data as { name: string; guide: number }[]).map(
      (item) => ({ name: item.name, value: item.guide })
    );
  }, [hallsApiData]);

  // ── Derived: admin-rooms chart data ───────────────────────────────────────
  const adminChartData = useMemo(() => {
    if (!adminRoomsApiData?.data) return [];
    return adminRoomsApiData.data.map((item: any) => ({
      name: item.name,
      Total: item.total ?? item.guide ?? 0,
    }));
  }, [adminRoomsApiData]);

  // ── Derived: support-facilities chart data ─────────────────────────────────
  const supportChartData = useMemo(() => {
    if (!supportFacilitiesApiData?.data) return [];
    return supportFacilitiesApiData.data.map((item: any) => ({
      name: item.name,
      Total: item.total ?? item.guide ?? 0,
    }));
  }, [supportFacilitiesApiData]);

  // ── Derived: staff-quarters chart data ─────────────────────────────────────
  const quartersChartData = useMemo(() => {
    if (!staffQuartersApiData?.data) return [];
    return staffQuartersApiData.data.map((item: any) => ({
      name: item.name,
      Total: item.total ?? item.guide ?? 0,
    }));
  }, [staffQuartersApiData]);

  // ── Derived: gap analysis filtered list ──────────────────────────────────
  const gapSchoolsFiltered = useMemo(() => {
    let schools: any[] = [];
    if (gapWise === 'classrooms') {
      schools = lowClassroomSchools;
    } else if (gapWise === 'labs') {
      schools = lowLabSchools;
    } else if (gapWise === 'adminrooms') {
      schools = lowAdminSchools;
    } else if (gapWise === 'supportfacilities') {
      schools = lowSupportSchools;
    } else if (gapWise === 'quarters') {
      schools = lowQuartersSchools;
    }

    if (!gapSearch.trim()) return schools;
    const q = gapSearch.toLowerCase();
    return schools.filter(
      (item: any) =>
        item.school?.schoolName?.toLowerCase().includes(q) ||
        item.school?.roName?.toLowerCase().includes(q) ||
        item.school?.stateName?.toLowerCase().includes(q)
    );
  }, [
    gapWise,
    gapSearch,
    lowClassroomSchools,
    lowLabSchools,
    lowAdminSchools,
    lowSupportSchools,
    lowQuartersSchools,
  ]);

  // ── Quick-access values from real API ────────────────────────────────────
  const totalClassrooms = classroomsApiData?.['Total Classrooms'];
  const totalLabs = labsApiData?.Total;
  const totalLibraries = librariesApiData?.Total;
  const totalHalls = hallsApiData?.Total;
  const totalSmartClassrooms = comparisonApiData?.Total?.SmartClassrooms;
  const totalComputerLabs =
    comparisonApiData?.Total?.ICT_Profile?.Labs?.Total_Computer_Labs;

  const totalAdminRooms = adminRoomsApiData?.Total;
  const totalSupportFacilities = supportFacilitiesApiData?.Total;
  const totalStaffQuarters = staffQuartersApiData?.Total;

  const getAdminRoomCount = (name: string) => {
    const item = adminRoomsApiData?.data?.find(
      (d: any) => d.name?.toLowerCase().includes(name.toLowerCase())
    );
    return item?.total ?? item?.guide;
  };

  const getSupportFacilityCount = (name: string) => {
    const item = supportFacilitiesApiData?.data?.find(
      (d: any) => d.name?.toLowerCase().includes(name.toLowerCase())
    );
    return item?.total ?? item?.guide;
  };

  const getStaffQuartersCount = (name: string) => {
    if (!staffQuartersApiData?.data) return undefined;
    const item = staffQuartersApiData.data.find(
      (d: any) => d.name?.toLowerCase().includes(name.toLowerCase())
    );
    if (item) return item.total ?? item.guide;

    // Index/substring fallbacks
    if (name.includes('Type 1') || name.toLowerCase().includes('grade d')) {
      const idx = staffQuartersApiData.data.findIndex(
        (d: any) =>
          d.name?.toLowerCase().includes('type 1') ||
          d.name?.toLowerCase().includes('type i') ||
          d.name?.toLowerCase().includes('grade d')
      );
      if (idx !== -1) return staffQuartersApiData.data[idx].total ?? staffQuartersApiData.data[idx].guide;
    }
    if (
      name.includes('Type 2') ||
      name.toLowerCase().includes('tgt') ||
      name.toLowerCase().includes('prt')
    ) {
      const idx = staffQuartersApiData.data.findIndex(
        (d: any) =>
          d.name?.toLowerCase().includes('type 2') ||
          d.name?.toLowerCase().includes('type ii') ||
          d.name?.toLowerCase().includes('tgt') ||
          d.name?.toLowerCase().includes('prt')
      );
      if (idx !== -1) return staffQuartersApiData.data[idx].total ?? staffQuartersApiData.data[idx].guide;
    }
    if (name.includes('Type 3') || name.toLowerCase().includes('pgt')) {
      const idx = staffQuartersApiData.data.findIndex(
        (d: any) =>
          d.name?.toLowerCase().includes('type 3') ||
          d.name?.toLowerCase().includes('type iii') ||
          d.name?.toLowerCase().includes('pgt')
      );
      if (idx !== -1) return staffQuartersApiData.data[idx].total ?? staffQuartersApiData.data[idx].guide;
    }
    if (name.includes('Type 4') || name.toLowerCase().includes('principal')) {
      const idx = staffQuartersApiData.data.findIndex(
        (d: any) =>
          d.name?.toLowerCase().includes('type 4') ||
          d.name?.toLowerCase().includes('type iv') ||
          d.name?.toLowerCase().includes('principal')
      );
      if (idx !== -1) return staffQuartersApiData.data[idx].total ?? staffQuartersApiData.data[idx].guide;
    }
    return undefined;
  };

  const mphCount = (hallsApiData?.data as any[])?.find(
    (d) => d.name === 'Multi Purpose Hall'
  )?.guide;
  const coveredCount = (hallsApiData?.data as any[])?.find(
    (d) => d.name === 'Assembly Stage (Covered)'
  )?.guide;
  const openCount = (hallsApiData?.data as any[])?.find(
    (d) => d.name === 'Assembly Stage (Open)'
  )?.guide;

  const pmClassrooms = comparisonApiData?.PMShree?.TotalClassrooms;
  const nonPmClassrooms = comparisonApiData?.['Non-PMShree']?.TotalClassrooms;
  const pmSmartClassrooms = comparisonApiData?.PMShree?.SmartClassrooms;
  const nonPmSmartClassrooms =
    comparisonApiData?.['Non-PMShree']?.SmartClassrooms;
  const pmComputerLabs =
    comparisonApiData?.PMShree?.ICT_Profile?.Labs?.Total_Computer_Labs;
  const nonPmComputerLabs =
    comparisonApiData?.['Non-PMShree']?.ICT_Profile?.Labs?.Total_Computer_Labs;

  // ── Helper: PM vs Non-PM split bar ───────────────────────────────────────
  const PmSplitBar = ({
    label,
    pm,
    non,
  }: {
    label: string;
    pm: number | undefined;
    non: number | undefined;
  }) => {
    const pmVal = pm || 0;
    const nonVal = non || 0;
    const total = pmVal + nonVal;
    const pmPct = total > 0 ? Math.round((pmVal / total) * 100) : 0;
    return (
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs font-semibold text-gray-700">
          <span>{label}</span>
          <span className="text-gray-400">{fmt(total)} total</span>
        </div>
        <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
          <div className="bg-[#6A1786] transition-all" style={{ width: `${pmPct}%` }} />
          <div className="bg-[#3B82F6] transition-all" style={{ width: `${100 - pmPct}%` }} />
        </div>
        <div className="flex justify-between text-[10px]">
          <span className="text-[#6A1786] font-bold">
            PM-SHRI: {fmt(pmVal)} ({pmPct}%)
          </span>
          <span className="text-[#3B82F6] font-bold">Non: {fmt(nonVal)}</span>
        </div>
      </div>
    );
  };

  // ── Pending-API notice banner ─────────────────────────────────────────────
  const PendingApiBanner = ({
    title,
    endpoints,
  }: {
    title: string;
    endpoints: string[];
  }) => (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4">
      <Clock className="w-7 h-7 text-amber-500 shrink-0 mt-0.5" />
      <div>
        <h4 className="font-bold text-amber-800">{title} — API Pending</h4>
        <p className="text-sm text-amber-700 mt-1">
          Real data requires new backend endpoint(s):
        </p>
        <div className="flex flex-wrap gap-2 mt-2">
          {endpoints.map((ep) => (
            <code
              key={ep}
              className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-mono"
            >
              {ep}
            </code>
          ))}
        </div>
      </div>
    </div>
  );

  // ── KVS standard target card ──────────────────────────────────────────────
  const TargetCard = ({
    name,
    qty,
    desc,
    color,
    actual,
  }: {
    name: string;
    qty: number;
    desc: string;
    color: string;
    actual?: number | string;
  }) => (
    <div
      className="bg-white p-5 rounded-2xl border shadow-sm hover:shadow-md transition-all animate-fadeIn"
      style={{ borderColor: `${color}30` }}
    >
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide leading-tight block">
        {name}
      </span>
      <p className="text-3xl font-extrabold mt-2 animate-pulseOnce" style={{ color }}>
        {actual !== undefined ? fmt(actual) : qty}
      </p>
      <p className="text-xs text-gray-500 mt-1">{desc}</p>
      {actual !== undefined ? (
        <span className="inline-block mt-2 px-2 py-0.5 bg-purple-50 text-[#6A1786] text-[10px] font-bold rounded">
          Target: {qty} / school
        </span>
      ) : (
        <span className="inline-block mt-2 px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded">
          KVS Target
        </span>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB] text-gray-900 font-sans">
      <Header />

      <div className="w-full max-w-7xl mx-auto px-4 pt-6">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* ── Loading ──────────────────────────────────────────────────────── */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 bg-white rounded-2xl shadow-sm border border-gray-100">
            <Loader2 className="w-12 h-12 text-[#6A1786] animate-spin mb-4" />
            <p className="text-gray-500 font-medium animate-pulse">
              Loading infrastructure analytics…
            </p>
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────────────────── */}
        {error && !loading && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-8 mb-6 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="w-8 h-8 text-rose-600" />
              <h3 className="text-xl font-bold text-rose-900">
                Error Loading Infrastructure Data
              </h3>
            </div>
            <p className="text-rose-700">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-5 px-6 py-2.5 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-all"
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Main content ─────────────────────────────────────────────────── */}
        {!loading && !error && (
          <>
            {/* Page header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
              <div>
                <h1 className="text-3xl font-extrabold text-[#6A1786] tracking-tight flex items-center gap-3">
                  <Building2 className="w-9 h-9 p-1.5 bg-[#E8D4F0] text-[#6A1786] rounded-xl" />
                  Infrastructure Analysis
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  Real-time infrastructure intelligence across the KVS network
                </p>
              </div>
              <button
                onClick={() => navigate('/kvs-samagam/analytics')}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:text-[#6A1786] hover:border-[#6A1786] font-semibold transition-all shadow-sm text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Analytics
              </button>
            </div>

            {/* Tab nav */}
            <div className="bg-white p-1.5 rounded-2xl border border-gray-200 shadow-sm flex overflow-x-auto gap-1 mb-8 scrollbar-hide">
              {[
                { id: 'overview', label: 'Overview', icon: Building2 },
                { id: 'academic', label: 'Academic & Classrooms', icon: Home },
                { id: 'labs', label: 'Laboratory Strength', icon: FlaskConical },
                { id: 'admin_support', label: 'Admin & Support', icon: Briefcase },
                {
                  id: 'residential_assembly',
                  label: 'Quarters & Assembly',
                  icon: Bed,
                },
                { id: 'gap_analysis', label: 'Gap Analysis', icon: AlertTriangle },
              ].map((tab) => {
                const Icon = tab.icon;
                const sel = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap rounded-xl transition-all ${
                      sel
                        ? 'bg-[#6A1786] text-white shadow-md shadow-[#6a1786]/20'
                        : 'text-gray-600 hover:text-[#6A1786] hover:bg-[#E8D4F0]/30'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* ═══════════ OVERVIEW ═══════════ */}
            {activeTab === 'overview' && (
              <div className="space-y-8 animate-fadeIn">
                {/* KPI cards */}
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Key Infrastructure Indicators
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      {
                        title: 'Total Classrooms',
                        value: fmt(totalClassrooms),
                        sub: '/totalclassroomskvs',
                        color: '#6A1786',
                        bg: '#E8D4F0',
                        icon: Home,
                      },
                      {
                        title: 'Smart Classrooms',
                        value: fmt(totalSmartClassrooms),
                        sub: 'All e-classroom types',
                        color: '#6366F1',
                        bg: '#E0E7FF',
                        icon: Sparkles,
                      },
                      {
                        title: 'Laboratories',
                        value: fmt(totalLabs),
                        sub: '7 lab types',
                        color: '#3B82F6',
                        bg: '#DBEAFE',
                        icon: FlaskConical,
                      },
                      {
                        title: 'Libraries',
                        value: fmt(totalLibraries),
                        sub: 'Library spaces',
                        color: '#10B981',
                        bg: '#D1FAE5',
                        icon: BookOpen,
                      },
                      {
                        title: 'Computer Labs',
                        value: fmt(totalComputerLabs),
                        sub: 'ICT labs (all KVS)',
                        color: '#F59E0B',
                        bg: '#FEF3C7',
                        icon: Building2,
                      },
                      {
                        title: 'MP Halls & Stages',
                        value: fmt(totalHalls),
                        sub: 'MPH + both stages',
                        color: '#14B8A6',
                        bg: '#CCFBF1',
                        icon: Building,
                      },
                    ].map((kpi, i) => {
                      const Icon = kpi.icon;
                      return (
                        <div
                          key={i}
                          className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide leading-tight">
                              {kpi.title}
                            </span>
                            <Icon
                              className="w-7 h-7 p-1.5 rounded-lg shrink-0"
                              style={{
                                color: kpi.color,
                                backgroundColor: kpi.bg,
                              }}
                            />
                          </div>
                          <span className="text-2xl font-extrabold text-gray-900">
                            {kpi.value}
                          </span>
                          <p className="text-[10px] text-gray-400 mt-1">{kpi.sub}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Lab type chart + PM comparison */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Bar chart */}
                  <div className="lg:col-span-3 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">
                      Laboratory Type Distribution
                    </h3>
                    <p className="text-sm text-gray-400 mb-6">
                      Total count per lab type across all KVS schools
                    </p>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={labTypeChartData}
                          layout="vertical"
                          margin={{ left: 10, right: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis
                            dataKey="name"
                            type="category"
                            tick={{ fontSize: 10, fontWeight: 600 }}
                            width={105}
                          />
                          <Tooltip formatter={(v) => [fmt(v), 'Labs']} />
                          <Bar
                            dataKey="Total"
                            radius={[0, 4, 4, 0]}
                            label={{
                              position: 'right',
                              fontSize: 11,
                              fontWeight: 700,
                              formatter: (v: any) => fmt(v),
                            }}
                          >
                            {labTypeChartData.map((_: any, i: number) => (
                              <Cell
                                key={i}
                                fill={CHART_COLORS[i % CHART_COLORS.length]}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* PM vs Non-PM split */}
                  <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-5">
                      PM-SHRI vs Non PM-SHRI
                    </h3>
                    <div className="space-y-5">
                      <PmSplitBar
                        label="Classrooms"
                        pm={pmClassrooms}
                        non={nonPmClassrooms}
                      />
                      <PmSplitBar
                        label="Smart Classrooms"
                        pm={pmSmartClassrooms}
                        non={nonPmSmartClassrooms}
                      />
                      <PmSplitBar
                        label="Computer Labs"
                        pm={pmComputerLabs}
                        non={nonPmComputerLabs}
                      />
                    </div>
                    <div className="mt-6 pt-4 border-t border-gray-50 flex gap-6">
                      {[
                        { label: 'PM-SHRI', color: '#6A1786' },
                        { label: 'Non PM-SHRI', color: '#3B82F6' },
                      ].map((leg) => (
                        <div key={leg.label} className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: leg.color }}
                          />
                          <span className="text-xs text-gray-500">{leg.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* KVS standard reference */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-purple-600" />
                    KVS Infrastructure Standard — Per School Baseline
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      {
                        label: 'Academic Spaces',
                        target: '53 Rooms',
                        desc: 'Classrooms, Library, Resource Rooms',
                      },
                      {
                        label: 'Laboratories',
                        target: '10 Labs',
                        desc: 'Science, Maths, Language, ICT',
                      },
                      {
                        label: 'Admin Rooms',
                        target: '6 Rooms',
                        desc: 'Principal, VP, Offices, Staff Room',
                      },
                      {
                        label: 'Support Facilities',
                        target: '9 Rooms',
                        desc: 'Medical, Sports, Guard Cabins',
                      },
                      {
                        label: 'Staff Quarters',
                        target: '19 Units',
                        desc: 'Type 1–4 residential quarters',
                      },
                      {
                        label: 'Assembly',
                        target: '3 Units',
                        desc: 'MPH, Covered & Open Stages',
                      },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="bg-purple-50/40 border border-purple-100 rounded-xl p-4"
                      >
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                          {item.label}
                        </p>
                        <p className="text-xl font-extrabold text-[#6A1786] mt-1">
                          {item.target}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total Schools from Redux */}
                {totalSchoolsFromRedux && (
                  <div className="bg-gradient-to-r from-[#6A1786] to-[#9E2AAB] p-6 rounded-2xl text-white shadow-md flex items-center justify-between">
                    <div>
                      <p className="text-purple-200 text-sm font-medium">
                        Total KVS Schools (Network)
                      </p>
                      <p className="text-5xl font-extrabold mt-1">
                        {fmt(totalSchoolsFromRedux)}
                      </p>
                    </div>
                    <Building2 className="w-16 h-16 text-white/20" />
                  </div>
                )}
              </div>
            )}

            {/* ═══════════ ACADEMIC & CLASSROOMS ═══════════ */}
            {activeTab === 'academic' && (
              <div className="space-y-6 animate-fadeIn">
                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    {
                      title: 'Total Classrooms',
                      value: fmt(totalClassrooms),
                      color: '#6A1786',
                      sub: 'Source: /totalclassroomskvs',
                    },
                    {
                      title: 'PM-SHRI Classrooms',
                      value: fmt(pmClassrooms),
                      color: '#10B981',
                      sub: 'PM-SHRI schools only',
                    },
                    {
                      title: 'Non PM-SHRI Classrooms',
                      value: fmt(nonPmClassrooms),
                      color: '#3B82F6',
                      sub: 'Non-PM-SHRI schools',
                    },
                    {
                      title: 'Smart Classrooms',
                      value: fmt(totalSmartClassrooms),
                      color: '#F59E0B',
                      sub: 'All e-classroom types',
                    },
                  ].map((c, i) => (
                    <div
                      key={i}
                      className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm"
                    >
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">
                        {c.title}
                      </p>
                      <p
                        className="text-4xl font-extrabold mt-2"
                        style={{ color: c.color }}
                      >
                        {c.value}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">{c.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Classroom type breakdown chart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">
                      Classroom Type Breakdown
                    </h3>
                    <p className="text-sm text-gray-400 mb-6">
                      PM-SHRI vs Non PM-SHRI by construction type
                    </p>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={classroomBreakdownData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [fmt(v), '']} />
                          <Legend />
                          <Bar
                            dataKey="PM-SHRI"
                            fill="#6A1786"
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar
                            dataKey="Non PM-SHRI"
                            fill="#3B82F6"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* E-classroom type details */}
                  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">
                      Smart Classroom Details
                    </h3>
                    <div className="space-y-4">
                      {[
                        {
                          label: 'Type A — Desktop + Projector + Board',
                          pm: comparisonApiData?.PMShree?.ICT_Profile
                            ?.E_Classrooms?.Type_A?.Count,
                          non: comparisonApiData?.['Non-PMShree']?.ICT_Profile
                            ?.E_Classrooms?.Type_A?.Count,
                        },
                        {
                          label: 'Type B — iPad + Apple TV',
                          pm: comparisonApiData?.PMShree?.ICT_Profile
                            ?.E_Classrooms?.Type_B?.Count,
                          non: comparisonApiData?.['Non-PMShree']?.ICT_Profile
                            ?.E_Classrooms?.Type_B?.Count,
                        },
                        {
                          label: 'Type C — Interactive Touch Panel',
                          pm: comparisonApiData?.PMShree?.ICT_Profile
                            ?.E_Classrooms?.Type_C?.Count,
                          non: comparisonApiData?.['Non-PMShree']?.ICT_Profile
                            ?.E_Classrooms?.Type_C?.Count,
                        },
                        {
                          label: 'Type D — Computer + Projector',
                          pm: comparisonApiData?.PMShree?.ICT_Profile
                            ?.E_Classrooms?.Type_D?.Count,
                          non: comparisonApiData?.['Non-PMShree']?.ICT_Profile
                            ?.E_Classrooms?.Type_D?.Count,
                        },
                      ].map((row, i) => (
                        <div
                          key={i}
                          className="border border-gray-100 rounded-xl p-4"
                        >
                          <p className="text-xs font-bold text-gray-600 mb-2">
                            {row.label}
                          </p>
                          <div className="flex gap-6">
                            {[
                              {
                                label: 'PM-SHRI',
                                val: row.pm,
                                color: '#6A1786',
                              },
                              {
                                label: 'Non PM-SHRI',
                                val: row.non,
                                color: '#3B82F6',
                              },
                              {
                                label: 'Total',
                                val: (row.pm || 0) + (row.non || 0),
                                color: '#374151',
                              },
                            ].map((cell) => (
                              <div key={cell.label}>
                                <p className="text-[10px] text-gray-400">
                                  {cell.label}
                                </p>
                                <p
                                  className="text-xl font-extrabold"
                                  style={{ color: cell.color }}
                                >
                                  {fmt(cell.val)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Library */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-6">
                  <BookOpen className="w-14 h-14 p-3 bg-emerald-50 text-emerald-600 rounded-2xl shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      Total Libraries
                    </h3>
                    <p className="text-5xl font-extrabold text-emerald-600 mt-1">
                      {fmt(totalLibraries)}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Library spaces across all KVS schools (source: /totallibraries)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════ LABORATORY STRENGTH ═══════════ */}
            {activeTab === 'labs' && (
              <div className="space-y-6 animate-fadeIn">
                {/* Lab type mini-cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                  {(labsApiData?.data || []).map((lab: any, i: number) => (
                    <div
                      key={i}
                      className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm text-center hover:shadow-md transition-all"
                    >
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide block mb-2 leading-tight">
                        {lab.name}
                      </span>
                      <span
                        className="text-2xl font-extrabold"
                        style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}
                      >
                        {fmt(lab.guide)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Lab strength horizontal bar chart */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-800 mb-1">
                    Laboratory Strength Overview
                  </h3>
                  <p className="text-sm text-gray-400 mb-6">
                    Total lab count by type · Source: /totallaboratoriessciencecomputerlabs
                  </p>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={labTypeChartData}
                        layout="vertical"
                        margin={{ left: 10, right: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          tick={{ fontSize: 11, fontWeight: 600 }}
                          width={110}
                        />
                        <Tooltip formatter={(v) => [fmt(v), 'Labs']} />
                        <Bar
                          dataKey="Total"
                          radius={[0, 4, 4, 0]}
                          label={{
                            position: 'right',
                            fontSize: 12,
                            fontWeight: 700,
                            formatter: (v: any) => fmt(v),
                          }}
                        >
                          {labTypeChartData.map((_: any, i: number) => (
                            <Cell
                              key={i}
                              fill={CHART_COLORS[i % CHART_COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Computer lab PM split */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {[
                    {
                      label: 'Total Computer Labs',
                      value: totalComputerLabs,
                      lan: comparisonApiData?.Total?.ICT_Profile?.Labs
                        ?.LAN_Connectivity_Labs,
                      net: comparisonApiData?.Total?.ICT_Profile?.Labs
                        ?.Internet_Connectivity_Labs,
                      color: '#6A1786',
                    },
                    {
                      label: 'PM-SHRI Computer Labs',
                      value: pmComputerLabs,
                      lan: comparisonApiData?.PMShree?.ICT_Profile?.Labs
                        ?.LAN_Connectivity_Labs,
                      net: comparisonApiData?.PMShree?.ICT_Profile?.Labs
                        ?.Internet_Connectivity_Labs,
                      color: '#10B981',
                    },
                    {
                      label: 'Non PM-SHRI Computer Labs',
                      value: nonPmComputerLabs,
                      lan: comparisonApiData?.['Non-PMShree']?.ICT_Profile?.Labs
                        ?.LAN_Connectivity_Labs,
                      net: comparisonApiData?.['Non-PMShree']?.ICT_Profile?.Labs
                        ?.Internet_Connectivity_Labs,
                      color: '#3B82F6',
                    },
                  ].map((card, i) => (
                    <div
                      key={i}
                      className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm"
                    >
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">
                        {card.label}
                      </p>
                      <p
                        className="text-4xl font-extrabold mt-2"
                        style={{ color: card.color }}
                      >
                        {fmt(card.value)}
                      </p>
                      <div className="mt-3 space-y-1">
                        <p className="text-xs text-gray-400">
                          LAN connected:{' '}
                          <span className="font-bold text-gray-700">
                            {fmt(card.lan)}
                          </span>
                        </p>
                        <p className="text-xs text-gray-400">
                          Internet connected:{' '}
                          <span className="font-bold text-gray-700">
                            {fmt(card.net)}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Top schools by labs */}
                {topLabSchools.length > 0 && (
                  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">
                      Top Schools by Laboratory Count
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            {[
                              'School',
                              'Region',
                              'State',
                              'Type',
                              'Total Labs',
                              'Lab Breakdown',
                            ].map((h) => (
                              <th
                                key={h}
                                className="py-3 px-4 text-left font-bold text-gray-500 uppercase"
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {topLabSchools.slice(0, 10).map((item: any, idx: number) => (
                            <tr
                              key={idx}
                              className="hover:bg-gray-50/50"
                            >
                              <td className="py-3 px-4 font-bold text-gray-800">
                                {item.school?.schoolName}
                              </td>
                              <td className="py-3 px-4 text-gray-600">
                                {item.school?.roName}
                              </td>
                              <td className="py-3 px-4 text-gray-600">
                                {item.school?.stateName}
                              </td>
                              <td className="py-3 px-4">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                    item.school?.isPMShree
                                      ? 'bg-green-100 text-emerald-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {item.school?.isPMShree ? 'PM-SHRI' : 'Regular'}
                                </span>
                              </td>
                              <td className="py-3 px-4 font-extrabold text-[#6A1786] text-sm">
                                {item.data?.totalLabs}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex flex-wrap gap-1">
                                  {(item.data?.labs || []).map(
                                    (lab: any, li: number) => (
                                      <span
                                        key={li}
                                        className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-semibold"
                                      >
                                        {lab.name}: {lab.quantity}
                                      </span>
                                    )
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══════════ ADMIN & SUPPORT ═══════════ */}
            {activeTab === 'admin_support' && (
              <div className="space-y-8 animate-fadeIn">
                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    {
                      title: 'Total Administrative Rooms',
                      value: fmt(totalAdminRooms),
                      color: '#4F46E5',
                      sub: 'Sourced from: /totaladministrativerooms',
                      icon: Building,
                    },
                    {
                      title: 'Total Support Facilities',
                      value: fmt(totalSupportFacilities),
                      color: '#0D9488',
                      sub: 'Sourced from: /totalsupportfacilities',
                      icon: Wrench,
                    },
                  ].map((c, i) => {
                    const Icon = c.icon;
                    return (
                      <div
                        key={i}
                        className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between"
                      >
                        <div>
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">
                            {c.title}
                          </p>
                          <p
                            className="text-4xl font-extrabold mt-2"
                            style={{ color: c.color }}
                          >
                            {c.value}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-1">{c.sub}</p>
                        </div>
                        <Icon className="w-12 h-12 text-gray-100 shrink-0" style={{ color: `${c.color}20` }} />
                      </div>
                    );
                  })}
                </div>

                {/* Distribution Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Admin Rooms Chart */}
                  {adminChartData.length > 0 && (
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-800 mb-1">
                        Administrative Rooms Distribution
                      </h3>
                      <p className="text-sm text-gray-400 mb-6">
                        Total count by administrative room type
                      </p>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={adminChartData}
                            layout="vertical"
                            margin={{ left: 10, right: 40 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis
                              dataKey="name"
                              type="category"
                              tick={{ fontSize: 10, fontWeight: 600 }}
                              width={110}
                            />
                            <Tooltip formatter={(v) => [fmt(v), 'Rooms']} />
                            <Bar
                              dataKey="Total"
                              fill="#6366F1"
                              radius={[0, 4, 4, 0]}
                              label={{
                                position: 'right',
                                fontSize: 11,
                                fontWeight: 700,
                                formatter: (v: any) => fmt(v),
                              }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Support Facilities Chart */}
                  {supportChartData.length > 0 && (
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-800 mb-1">
                        Support Facilities Distribution
                      </h3>
                      <p className="text-sm text-gray-400 mb-6">
                        Total count by support facility type
                      </p>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={supportChartData}
                            layout="vertical"
                            margin={{ left: 10, right: 40 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis
                              dataKey="name"
                              type="category"
                              tick={{ fontSize: 10, fontWeight: 600 }}
                              width={120}
                            />
                            <Tooltip formatter={(v) => [fmt(v), 'Facilities']} />
                            <Bar
                              dataKey="Total"
                              fill="#14B8A6"
                              radius={[0, 4, 4, 0]}
                              label={{
                                position: 'right',
                                fontSize: 11,
                                fontWeight: 700,
                                formatter: (v: any) => fmt(v),
                              }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>

                {/* Admin rooms standard */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5 text-indigo-600" />
                    Administrative Rooms Breakdown
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      {
                        name: 'Principal Room',
                        qty: 1,
                        desc: 'Private principal office',
                      },
                      {
                        name: 'Vice Principal Room',
                        qty: 1,
                        desc: 'VP cabin & meeting space',
                      },
                      {
                        name: 'Headmaster Room',
                        qty: 1,
                        desc: 'HM room for primary wing',
                      },
                      {
                        name: 'Office Room',
                        qty: 1,
                        desc: 'Clerical & records office',
                      },
                      {
                        name: 'Exam Control Room',
                        qty: 1,
                        desc: 'Secure evaluation control',
                      },
                      {
                        name: 'Staff Common Room',
                        qty: 2,
                        desc: 'Common room for teachers',
                      },
                    ].map((r, i) => (
                      <TargetCard
                        key={i}
                        name={r.name}
                        qty={r.qty}
                        desc={r.desc}
                        color="#6366F1"
                        actual={getAdminRoomCount(r.name)}
                      />
                    ))}
                  </div>
                </div>

                {/* Support facilities standard */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-teal-600" />
                    Support Facilities Breakdown
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      {
                        name: 'Medical Room',
                        qty: 1,
                        desc: 'First-aid & nursery space',
                      },
                      {
                        name: 'Sports Room',
                        qty: 1,
                        desc: 'Sports goods & gear store',
                      },
                      {
                        name: 'Store Room',
                        qty: 1,
                        desc: 'General hardware store',
                      },
                      {
                        name: 'Guard Room',
                        qty: 2,
                        desc: 'Security cabins at gates',
                      },
                      {
                        name: 'WE Workshop',
                        qty: 1,
                        desc: 'Work experience learning lab',
                      },
                      {
                        name: 'Music Room',
                        qty: 1,
                        desc: 'Support music club facility',
                      },
                    ].map((r, i) => (
                      <TargetCard
                        key={i}
                        name={r.name}
                        qty={r.qty}
                        desc={r.desc}
                        color="#14B8A6"
                        actual={getSupportFacilityCount(r.name)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════ RESIDENTIAL & ASSEMBLY ═══════════ */}
            {activeTab === 'residential_assembly' && (
              <div className="space-y-6 animate-fadeIn">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    Assembly & Multi-Purpose Halls{' '}
                    <span className="text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                      Real API Data
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      {
                        label: 'Multi Purpose Halls',
                        value: mphCount,
                        color: '#6A1786',
                        desc: 'Multi-event assembly halls',
                      },
                      {
                        label: 'Assembly Stage (Covered)',
                        value: coveredCount,
                        color: '#10B981',
                        desc: 'Indoor covered stages',
                      },
                      {
                        label: 'Assembly Stage (Open)',
                        value: openCount,
                        color: '#3B82F6',
                        desc: 'Ground assembly stages',
                      },
                    ].map((card, i) => (
                      <div
                        key={i}
                        className="bg-white p-7 rounded-2xl border border-gray-100 shadow-sm text-center hover:shadow-md transition-all"
                      >
                        <span className="text-sm font-bold text-gray-500">
                          {card.label}
                        </span>
                        <p
                          className="text-5xl font-extrabold mt-3"
                          style={{ color: card.color }}
                        >
                          {fmt(card.value)}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">{card.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Assembly pie */}
                {assemblyPieData.length > 0 && (
                  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">
                      Assembly Infrastructure Distribution
                    </h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={assemblyPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={4}
                            dataKey="value"
                            label={({ name, value }) =>
                              `${name}: ${fmt(value)}`
                            }
                            labelLine
                          >
                            {assemblyPieData.map((_: any, i: number) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => [fmt(v), 'Count']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Staff Quarters — Real API data */}
                <div className="border-t border-gray-100 pt-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Bed className="w-5 h-5 text-[#6A1786]" />
                    Staff Quarters Counts & Distribution
                  </h3>

                  {/* Quarters Total KPI */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between col-span-1">
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">
                          Total Staff Quarters
                        </p>
                        <p className="text-4xl font-extrabold mt-2 text-[#6A1786]">
                          {fmt(totalStaffQuarters)}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">Sourced from: /totalstaffquarters</p>
                      </div>
                      <Bed className="w-12 h-12 text-[#6A1786]/20 shrink-0" />
                    </div>

                    {/* Quarters Chart */}
                    {quartersChartData.length > 0 && (
                      <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm col-span-2 h-44 flex flex-col justify-center">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                          Quarters Type Breakdown
                        </p>
                        <div className="h-28">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={quartersChartData}
                              layout="vertical"
                              margin={{ left: 10, right: 40 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                              <XAxis type="number" tick={{ fontSize: 10 }} />
                              <YAxis
                                dataKey="name"
                                type="category"
                                tick={{ fontSize: 10, fontWeight: 600 }}
                                width={120}
                              />
                              <Tooltip formatter={(v) => [fmt(v), 'Units']} />
                              <Bar
                                dataKey="Total"
                                fill="#6A1786"
                                radius={[0, 4, 4, 0]}
                                label={{
                                  position: 'right',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  formatter: (v: any) => fmt(v),
                                }}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quarters breakdown list */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      {
                        name: 'Type 1 — Grade D Staff',
                        qty: 2,
                        desc: 'Grade D staff accommodation',
                      },
                      {
                        name: 'Type 2 — TGT / PRT',
                        qty: 8,
                        desc: 'Teacher grade residential',
                      },
                      {
                        name: 'Type 3 — PGT',
                        qty: 8,
                        desc: 'Senior teacher residential',
                      },
                      {
                        name: 'Type 4 — Principal',
                        qty: 1,
                        desc: 'Principal bungalow unit',
                      },
                    ].map((q, i) => (
                      <TargetCard
                        key={i}
                        name={q.name}
                        qty={q.qty}
                        desc={q.desc}
                        color="#6A1786"
                        actual={getStaffQuartersCount(q.name)}
                      />
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-gray-400 flex items-center gap-1.5">
                    <Info className="w-4 h-4" />
                    Total target: 19 quarters per school (2 + 8 + 8 + 1)
                  </p>
                </div>
              </div>
            )}

            {/* ═══════════ GAP ANALYSIS ═══════════ */}
            {activeTab === 'gap_analysis' && (
              <div className="space-y-6 animate-fadeIn">
                {/* Controls */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">
                        Infrastructure Gap Analysis
                      </h3>
                      <p className="text-sm text-gray-400 mt-0.5">
                        Schools with the lowest infrastructure counts — sourced
                        directly from API
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                      {/* Category toggle */}
                      <div className="flex bg-gray-100 rounded-xl p-1 gap-1 overflow-x-auto scrollbar-hide">
                        {(
                          [
                            { id: 'classrooms', label: 'Classrooms' },
                            { id: 'labs', label: 'Labs' },
                            { id: 'adminrooms', label: 'Admin Rooms' },
                            { id: 'supportfacilities', label: 'Support Facilities' },
                            { id: 'quarters', label: 'Staff Quarters' },
                          ] as const
                        ).map((opt) => (
                          <button
                            key={opt.id}
                            onClick={() => setGapWise(opt.id)}
                            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                              gapWise === opt.id
                                ? 'bg-[#6A1786] text-white shadow'
                                : 'text-gray-600 hover:text-[#6A1786]'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search school / region…"
                          value={gapSearch}
                          onChange={(e) => setGapSearch(e.target.value)}
                          className="pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#6A1786] w-full sm:w-52"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Results count badge */}
                  <div className="mb-4 flex items-center gap-2">
                    <span className="px-3 py-1 bg-rose-50 text-rose-700 text-xs font-bold rounded-full border border-rose-100">
                      {gapSchoolsFiltered.length} schools listed
                    </span>
                    <span className="text-xs text-gray-400">
                      (page 1 of results · sorted by lowest count first)
                    </span>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="py-3 px-4 text-left font-bold text-gray-500 uppercase">
                            School
                          </th>
                          <th className="py-3 px-4 text-left font-bold text-gray-500 uppercase">
                            Region
                          </th>
                          <th className="py-3 px-4 text-left font-bold text-gray-500 uppercase">
                            State
                          </th>
                          <th className="py-3 px-4 text-center font-bold text-gray-500 uppercase">
                            Type
                          </th>
                          {gapWise === 'classrooms' ? (
                            <th className="py-3 px-4 text-center font-bold text-gray-500 uppercase">
                              Classrooms
                            </th>
                          ) : (
                            <>
                              <th className="py-3 px-4 text-center font-bold text-gray-500 uppercase">
                                {gapWise === 'labs' && 'Total Labs'}
                                {gapWise === 'adminrooms' && 'Admin Rooms'}
                                {gapWise === 'supportfacilities' && 'Support Facilities'}
                                {gapWise === 'quarters' && 'Staff Quarters'}
                              </th>
                              <th className="py-3 px-4 text-left font-bold text-gray-500 uppercase">
                                Breakdown / Types Present
                              </th>
                            </>
                          )}
                          <th className="py-3 px-4 text-center font-bold text-gray-500 uppercase">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {gapSchoolsFiltered.length === 0 ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="py-16 text-center text-gray-400"
                            >
                              No schools found
                            </td>
                          </tr>
                        ) : (
                          gapSchoolsFiltered.map((item: any, idx: number) => (
                            <tr key={idx} className="hover:bg-gray-50/50">
                              <td className="py-3.5 px-4 font-bold text-gray-800">
                                {item.school?.schoolName}
                              </td>
                              <td className="py-3.5 px-4 text-gray-600">
                                {item.school?.roName}
                              </td>
                              <td className="py-3.5 px-4 text-gray-600">
                                {item.school?.stateName}
                              </td>
                              <td className="py-3.5 px-4 text-center">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                    item.school?.isPMShree
                                      ? 'bg-green-100 text-emerald-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {item.school?.isPMShree
                                    ? 'PM-SHRI'
                                    : 'Regular'}
                                </span>
                              </td>

                              {gapWise === 'classrooms' ? (
                                <td className="py-3.5 px-4 text-center">
                                  <span className="text-rose-600 font-extrabold text-base">
                                    {item.data?.totalClassrooms ?? item.score}
                                  </span>
                                  <span className="text-gray-400 text-[10px] ml-1">
                                    / 48 target
                                  </span>
                                </td>
                              ) : (
                                <>
                                  <td className="py-3.5 px-4 text-center">
                                    <span className="text-rose-600 font-extrabold text-base">
                                      {item.data?.totalLabs ??
                                        item.data?.totalSupportFacilities ??
                                        item.data?.totalAdministrativeRooms ??
                                        item.data?.totalQuarters ??
                                        item.score}
                                    </span>
                                    <span className="text-gray-400 text-[10px] ml-1">
                                      {gapWise === 'labs' && '/ 10 target'}
                                      {gapWise === 'adminrooms' && '/ 6 target'}
                                      {gapWise === 'supportfacilities' && '/ 9 target'}
                                      {gapWise === 'quarters' && '/ 19 target'}
                                    </span>
                                  </td>
                                  <td className="py-3.5 px-4">
                                    <div className="flex flex-wrap gap-1">
                                      {(item.data?.breakdown ?? item.data?.labs ?? []).map(
                                        (lab: any, li: number) => (
                                          <span
                                            key={li}
                                            className="px-1.5 py-0.5 bg-rose-50 text-rose-600 rounded text-[10px] font-semibold"
                                          >
                                            {lab.name}: {lab.quantity}
                                          </span>
                                        )
                                      )}
                                      {(item.data?.breakdown ?? item.data?.labs ?? []).length === 0 && (
                                        <span className="text-gray-400 italic">
                                          None recorded
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </>
                              )}

                              <td className="py-3.5 px-4 text-center">
                                <button
                                  onClick={() =>
                                    setSelectedSchool({
                                      schoolID: item.school?.schoolID,
                                      schoolName: item.school?.schoolName,
                                    })
                                  }
                                  className="px-3 py-1.5 bg-purple-50 text-[#6A1786] font-bold rounded-lg hover:bg-purple-100 transition-all text-[11px]"
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Detailed Analysis Reports ─────────────────────────────── */}
            <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Detailed Analysis Reports
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                Access comprehensive analysis for each category
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {[
                  {
                    name: 'Infrastructure',
                    path: '/kvs-samagam/analytics/infrastructure',
                    icon: '🏫',
                  },
                  {
                    name: 'Enrollment',
                    path: '/kvs-samagam/analytics/enrollment',
                    icon: '👨‍🎓',
                  },
                  {
                    name: 'ICT Profile',
                    path: '/kvs-samagam/analytics/ict-profile',
                    icon: '💻',
                  },
                  {
                    name: 'Library Profile',
                    path: '/kvs-samagam/analytics/library-profile',
                    icon: '📚',
                  },
                  {
                    name: 'CWSN Profile',
                    path: '/kvs-samagam/analytics/cwsn-profile',
                    icon: '♿',
                  },
                  {
                    name: 'NCC Profile',
                    path: '/kvs-samagam/analytics/ncc-profile',
                    icon: '🎖️',
                  },
                  {
                    name: 'Sports',
                    path: '/kvs-samagam/analytics/sports',
                    icon: '🏆',
                  },
                  {
                    name: 'Green Building',
                    path: '/kvs-samagam/analytics/green-building',
                    icon: '🌿',
                  },
                  {
                    name: 'Safety',
                    path: '/kvs-samagam/analytics/safety',
                    icon: '🛡️',
                  },
                  {
                    name: 'Special Labs',
                    path: '/kvs-samagam/analytics/special-labs',
                    icon: '🔬',
                  },
                ].map((item) => {
                  const isCurrent = item.name === 'Infrastructure';
                  return (
                    <button
                      key={item.name}
                      onClick={() => !isCurrent && navigate(item.path)}
                      disabled={isCurrent}
                      className={`rounded-lg p-4 border border-gray-200 text-center transition-all ${
                        isCurrent
                          ? 'opacity-50 cursor-not-allowed bg-gray-50'
                          : 'hover:shadow-lg cursor-pointer hover:scale-105 bg-white'
                      }`}
                    >
                      <span className="text-3xl">{item.icon}</span>
                      <p className="text-sm font-medium text-gray-700 mt-2">
                        {item.name}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />

      {selectedSchool && (
        <SchoolDetailsModal
          schoolID={selectedSchool.schoolID}
          onClose={() => setSelectedSchool(null)}
          selectedCategory="Infrastructure"
        />
      )}
    </div>
  );
}
